import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { 
  Phone, 
  CheckCircle2, 
  AlertTriangle, 
  MapPin, 
  ArrowLeft, 
  ArrowRight,
  Shield,
  Calendar,
  Clock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Layout } from '../components/layout/Layout';
import { SEO } from '../components/shared/SEO';
import { CTASection } from '../components/shared/CTASection';
import { getServiceById, getRelatedServices, SERVICE_AREAS, COMPANY_INFO } from '../data/mock';

export default function ServiceDetail() {
  const { serviceId } = useParams();
  const service = getServiceById(serviceId);
  const relatedServices = getRelatedServices(serviceId);

  if (!service) {
    return <Navigate to="/services" replace />;
  }

  const isEmergency = serviceId === 'emergency-repair';

  return (
    <Layout>
      <SEO
        title={`${service.h1} | AAA Irrigation Service`}
        description={`${service.intro.substring(0, 155)}...`}
        keywords={`${service.title.toLowerCase()} Plano TX, ${service.title.toLowerCase()} Allen TX, ${service.title.toLowerCase()} Frisco, TCEQ licensed irrigation, North Texas sprinkler repair`}
      />
      {/* Hero Section */}
      <section className="relative bg-slate-900 py-16 md:py-24 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src={service.heroImage}
            alt={`${service.title} - Professional irrigation service in Allen &amp; North Texas`}
            className="w-full h-full object-cover opacity-30"
            fetchpriority="high"
            width="1920"
            height="600"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/80 to-slate-900/60"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav className="mb-8">
            <Link
              to="/services"
              className="inline-flex items-center gap-2 text-slate-400 hover:text-teal-400 transition-colors duration-200"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>All Services</span>
            </Link>
          </nav>

          <div className="max-w-4xl">
            <div className="flex items-center gap-3 mb-6">
              <a 
                href={COMPANY_INFO.licenseUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-teal-500/20 border border-teal-500/30 rounded-full px-4 py-1.5 hover:bg-teal-500/30 transition-colors"
              >
                <Shield className="w-4 h-4 text-teal-400" />
                <span className="text-sm text-teal-400 font-medium">TCEQ Licensed</span>
              </a>
              {isEmergency && (
                <span className="inline-flex items-center gap-2 bg-amber-500/20 border border-amber-500/30 rounded-full px-4 py-1.5">
                  <Clock className="w-4 h-4 text-amber-400" />
                  <span className="text-sm text-amber-400 font-medium">24/7 Available</span>
                </span>
              )}
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              {service.h1}
            </h1>

            <p className="text-lg md:text-xl text-slate-300 mb-8 leading-relaxed max-w-3xl">
              {service.intro}
            </p>

            <div className="flex flex-wrap items-center gap-4">
              <Button
                asChild
                size="lg"
                className="bg-teal-500 hover:bg-teal-600 text-white shadow-lg shadow-teal-500/30 hover:shadow-teal-500/50 transition-all duration-300"
              >
                <a href={`tel:${COMPANY_INFO.phone}`}>
                  <Phone className="w-4 h-4 mr-2" />
                  Call {COMPANY_INFO.phoneFormatted}
                </a>
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white transition-all duration-300"
              >
                <Link to={COMPANY_INFO.bookingUrl}>
                  <Calendar className="w-4 h-4 mr-2" />
                  Schedule Service
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Left Column - Main Content */}
            <div className="lg:col-span-2 space-y-12">
              {/* What We Do */}
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">
                  What We Do
                </h2>
                <ul className="space-y-3">
                  {service.services.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-teal-500 mt-0.5 flex-shrink-0" />
                      <span className="text-slate-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Signs You Need This Service */}
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">
                  Signs You Need This Service
                </h2>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
                  <ul className="space-y-3">
                    {service.signs.map((sign, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                        <span className="text-slate-700">{sign}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* FAQ Section */}
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">
                  Frequently Asked Questions
                </h2>
                {service.faqs?.length > 0 && (
                  <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                      __html: JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "FAQPage",
                        "mainEntity": service.faqs.map(f => ({
                          "@type": "Question",
                          "name": f.question,
                          "acceptedAnswer": { "@type": "Answer", "text": f.answer }
                        }))
                      })
                    }}
                  />
                )}
                <Accordion type="single" collapsible className="w-full">
                  {service.faqs.map((faq, index) => (
                    <AccordionItem key={index} value={`item-${index}`} className="border-slate-200">
                      <AccordionTrigger className="text-left text-slate-900 hover:text-teal-600 font-medium py-4">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-slate-600 pb-4">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </div>

            {/* Right Column - Sidebar */}
            <div className="lg:col-span-1 space-y-6">
              {/* Contact Card */}
              <Card className="border-teal-200 bg-gradient-to-br from-teal-50 to-cyan-50 sticky top-24">
                <CardHeader>
                  <CardTitle className="text-xl text-slate-900">
                    Get Help Today
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-slate-600">
                    Call now for fast, professional service from our TCEQ licensed technicians.
                  </p>
                  <Button
                    asChild
                    className="w-full bg-teal-600 hover:bg-teal-700 text-white shadow-lg shadow-teal-600/20 hover:shadow-teal-600/40 transition-all duration-300"
                  >
                    <a href={`tel:${COMPANY_INFO.phone}`}>
                      <Phone className="w-4 h-4 mr-2" />
                      {COMPANY_INFO.phoneFormatted}
                    </a>
                  </Button>
                  <div className="text-sm text-slate-500 space-y-1">
                    <p>{COMPANY_INFO.hours.weekday}</p>
                    <p>{COMPANY_INFO.hours.saturday}</p>
                    <p>{COMPANY_INFO.hours.sunday}</p>
                  </div>
                </CardContent>
              </Card>

              {/* Service Areas */}
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle className="text-lg text-slate-900 flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-teal-500" />
                    Service Areas
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {SERVICE_AREAS.map((area) => (
                      <span
                        key={area}
                        className="inline-block bg-slate-100 text-slate-700 text-sm px-3 py-1 rounded-full"
                      >
                        {area}
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Related Services */}
              {relatedServices.length > 0 && (
                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle className="text-lg text-slate-900">
                      Related Services
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {relatedServices.map((related) => (
                      <Link
                        key={related.id}
                        to={`/services/${related.id}`}
                        className="group flex items-center justify-between p-3 bg-slate-50 hover:bg-teal-50 rounded-lg transition-colors duration-200"
                      >
                        <span className="text-slate-700 group-hover:text-teal-700 font-medium">
                          {related.title}
                        </span>
                        <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-teal-500 group-hover:translate-x-1 transition-all duration-200" />
                      </Link>
                    ))}
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Back to Services Link */}
      <section className="py-8 bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/services"
            className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-medium transition-colors duration-200"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to All Services</span>
          </Link>
        </div>
      </section>

      {/* CTA Section */}
      <CTASection variant={isEmergency ? 'emergency' : 'default'} />
    </Layout>
  );
}
